/**
 * AccountMenu — Radix Dropdown from top-right avatar.
 *
 * Two sections:
 *   PERSÖNLICH: Profil, Einstellungen, Benachrichtigungen
 *   BETRIEB & TEAM: Mitglieder, Abonnement, Betriebsverwaltung (Console)
 *
 * Plus: Hilfe, Datenschutz, AGB, Impressum, Abmelden
 *
 * Replaces: onProfileClick → settingsView='profile' direct jump.
 * Centralizes account IA — no more Mehr/Profil overlap.
 */

import { useTranslation } from 'react-i18next';
import { Dropdown } from './overlay';

export default function AccountMenu({
  user,
  onOpenSettings,
  onNavigate,
  onLogout,
  children,
}) {
  const { t } = useTranslation();
  const isAdmin = user && ['tenant_admin', 'alcedo_operator'].includes(user.role);

  const initials = (user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        {children}
      </Dropdown.Trigger>
      <Dropdown.Content side="bottom" align="end" sideOffset={4} className="w-[240px]" data-testid="account-menu" ariaLabel={t('account.menuLabel', 'Konto-Menü')}>
        {/* User header */}
        <div className="px-3 py-2.5 border-b border-[var(--aif-border-default)]">
          <div className="flex items-center gap-3">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[var(--aif-color-primary)] flex items-center justify-center flex-shrink-0">
                <span className="text-[var(--aif-text-on-primary,#fff)] font-semibold text-sm">{initials}</span>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-[var(--aif-text-primary)] truncate">{user?.name || 'User'}</div>
              <div className="text-[11px] text-[var(--aif-text-muted)] truncate">{user?.email || ''}</div>
            </div>
          </div>
        </div>

        {/* PERSÖNLICH */}
        <Dropdown.Label data-testid="account-section-personal">
          {t('account.sectionPersonal', 'Persönlich')}
        </Dropdown.Label>
        <Dropdown.Item
          onSelect={() => onOpenSettings('profile')}
          leadingIcon={<UserIcon />}
          data-testid="account-menu-profile"
        >
          {t('menu.profile', 'Profil')}
        </Dropdown.Item>
        <Dropdown.Item
          onSelect={() => onOpenSettings('settings')}
          leadingIcon={<SettingsIcon />}
          data-testid="account-menu-settings"
        >
          {t('menu.settings', 'Einstellungen')}
        </Dropdown.Item>
        <Dropdown.Item
          onSelect={() => onOpenSettings('notifications')}
          leadingIcon={<BellIcon />}
          data-testid="account-menu-notifications"
        >
          {t('settings.notifications', 'Benachrichtigungen')}
        </Dropdown.Item>

        <Dropdown.Separator />

        {/* BETRIEB & TEAM */}
        <Dropdown.Label data-testid="account-section-team">
          {t('account.sectionTeam', 'Betrieb & Team')}
        </Dropdown.Label>
        <Dropdown.Item
          onSelect={() => onOpenSettings('members')}
          leadingIcon={<TeamIcon />}
          data-testid="account-menu-members"
        >
          {t('menu.team', 'Team verwalten')}
        </Dropdown.Item>
        <Dropdown.Item
          onSelect={() => onOpenSettings('subscription')}
          leadingIcon={<CreditCardIcon />}
          data-testid="account-menu-subscription"
        >
          {t('menu.subscription', 'Abonnement')}
        </Dropdown.Item>
        {isAdmin && (
          <Dropdown.Item
            onSelect={() => onOpenSettings('console')}
            leadingIcon={<ConsoleIcon />}
            data-testid="account-menu-console"
          >
            {t('menu.console', 'Admin Console')}
          </Dropdown.Item>
        )}

        <Dropdown.Separator />

        {/* Footer links */}
        <Dropdown.Item
          onSelect={() => onOpenSettings('help')}
          leadingIcon={<HelpIcon />}
          data-testid="account-menu-help"
        >
          {t('menu.help', 'Hilfe')}
        </Dropdown.Item>
        <Dropdown.Item
          onSelect={() => onNavigate('/datenschutz')}
          leadingIcon={<ShieldIcon />}
          className="text-[var(--aif-text-secondary)]"
        >
          {t('menu.privacy', 'Datenschutz')}
        </Dropdown.Item>
        <Dropdown.Item
          onSelect={() => onNavigate('/agb')}
          leadingIcon={<FileIcon />}
          className="text-[var(--aif-text-secondary)]"
        >
          {t('menu.terms', 'AGB')}
        </Dropdown.Item>
        <Dropdown.Item
          onSelect={() => onNavigate('/impressum')}
          leadingIcon={<InfoIcon />}
          className="text-[var(--aif-text-secondary)]"
        >
          {t('menu.imprint', 'Impressum')}
        </Dropdown.Item>

        <Dropdown.Separator />

        <Dropdown.Item
          onSelect={onLogout}
          leadingIcon={<LogoutIcon />}
          data-testid="account-menu-logout"
        >
          {t('auth.logout', 'Abmelden')}
        </Dropdown.Item>
      </Dropdown.Content>
    </Dropdown.Root>
  );
}

// ─── Icons (heroicons outline, 16×16) ────────────────────────
function UserIcon() {
  return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>;
}
function SettingsIcon() {
  return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
}
function BellIcon() {
  return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" /></svg>;
}
function TeamIcon() {
  return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M18 21a8 8 0 00-16 0M10 14a4 4 0 100-8 4 4 0 000 8zm10-3a3 3 0 11-6 0 3 3 0 016 0zm-2 10a6 6 0 00-6-6" /></svg>;
}
function CreditCardIcon() {
  return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" /></svg>;
}
function ConsoleIcon() {
  return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" /></svg>;
}
function HelpIcon() {
  return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827m0 3v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
}
function ShieldIcon() {
  return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>;
}
function FileIcon() {
  return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>;
}
function InfoIcon() {
  return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>;
}
function LogoutIcon() {
  return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>;
}
